import { CARD_API_ENDPOINT } from '@/config/consts'
import { useMutation } from '@tanstack/react-query'
import { useAccount } from 'wagmi'
import { type AccountStatus, CardEligibilityResult, KYCStatus, WaitlistEntry } from '../types/CardState'
import { createSmartFetch, createSmartGet } from './useAuthenticatedMutation'
import { useCardAuth } from './useCardAuth'

interface CheckAndOnboardStatus {
  walletAddress: string
  status: string
  kycStatus?: string
  maxCards?: number
  fastLane?: boolean
}

interface CheckAndOnboardResponse {
  data: {
    status: CheckAndOnboardStatus
    waitlistEntry?: WaitlistEntry
    autoOnboard: boolean
  }
}

interface UserStatusResponse {
  kycStatus?: string
  rainStatus?: string
}

const postEligibilityCheck = createSmartFetch(`${CARD_API_ENDPOINT}/v1/vendor/cards/eligibility/check`, {
  method: 'POST',
})
const getUserStatus = createSmartGet(`${CARD_API_ENDPOINT}/v1/vendor/cards/users/status`)

function parseKycStatus(raw: string | undefined, fallback: KYCStatus): KYCStatus {
  if (!raw) return fallback
  return Object.values(KYCStatus).includes(raw as KYCStatus) ? (raw as KYCStatus) : fallback
}

export function computeCardEligibilityResult(params: {
  backendStatus: string
  kycStatus: KYCStatus
  rainStatus?: string
  hasExistingCards: boolean
  waitlistEntry?: WaitlistEntry
  autoOnboard: boolean
  fastLane?: boolean
  maxCards?: number
}): CardEligibilityResult {
  const { backendStatus, kycStatus, rainStatus, hasExistingCards, waitlistEntry, autoOnboard, fastLane, maxCards } =
    params

  const isEligible = backendStatus === 'active' || backendStatus === 'waitlisted'

  return {
    isEligible,
    isRequiredKYC: kycStatus !== KYCStatus.APPROVED,
    kycStatus,
    rainStatus,
    agreementsAccepted: hasExistingCards || undefined,
    waitlistEntry,
    autoOnboard,
    fastLane,
    accountStatus: backendStatus as AccountStatus,
    maxCards,
  }
}

export async function checkCardEligibility(referralCode?: string): Promise<CardEligibilityResult> {
  const body: Record<string, string> = {}
  if (referralCode) {
    body.referralCode = referralCode
  }

  const response: CheckAndOnboardResponse = await postEligibilityCheck(Object.keys(body).length > 0 ? body : undefined)

  const { status: backendStatus, waitlistEntry, autoOnboard } = response.data

  let kycStatus = parseKycStatus(backendStatus.kycStatus, KYCStatus.NOT_REQUESTED)

  let hasExistingCards = false
  const getCards = createSmartGet(`${CARD_API_ENDPOINT}/v1/vendor/cards`)
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const cardsResponse = await getCards()
      const cards = cardsResponse.data || []
      hasExistingCards = Array.isArray(cards) && cards.length > 0
      break
    } catch {
      if (attempt === 0) await new Promise((r) => setTimeout(r, 500))
    }
  }

  // The eligibility endpoint often omits kycStatus. Fetch from /users/status
  // to get the real KYC + Rain status when needed.
  let rainStatus: string | undefined
  const needsUserStatus = !backendStatus.kycStatus || kycStatus === KYCStatus.APPROVED
  if (needsUserStatus) {
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const statusResponse = await getUserStatus()
        const statusData: UserStatusResponse = statusResponse.data
        if (statusData.kycStatus) {
          kycStatus = parseKycStatus(statusData.kycStatus, kycStatus)
        }
        rainStatus = statusData.rainStatus
        break
      } catch {
        if (attempt === 0) await new Promise((r) => setTimeout(r, 1000))
      }
    }
  }

  return computeCardEligibilityResult({
    backendStatus: backendStatus.status,
    kycStatus,
    rainStatus,
    hasExistingCards,
    waitlistEntry,
    autoOnboard,
    fastLane: backendStatus.fastLane,
    maxCards: backendStatus.maxCards,
  })
}

export function useCardEligibility() {
  const { address } = useAccount()
  const { login, isLoading: authLoading, hasValidTokens } = useCardAuth()

  const mutation = useMutation({
    mutationFn: async (referralCode?: string) => {
      if (!address) {
        throw new Error('Wallet not connected')
      }

      if (!hasValidTokens()) {
        await login()
      }

      return checkCardEligibility(referralCode)
    },
    retry: (failureCount, error) => {
      const msg = error instanceof Error ? error.message : ''
      if (msg.includes('CORS') || msg.includes('User rejected')) {
        return false
      }
      return failureCount < 2
    },
  })

  const kycStatusMutation = useMutation({
    mutationFn: async () => {
      if (!hasValidTokens()) {
        await login()
      }

      const response = await getUserStatus()
      const statusData: UserStatusResponse = response.data

      return {
        kycStatus: parseKycStatus(statusData.kycStatus, KYCStatus.PENDING),
        rainStatus: statusData.rainStatus,
      }
    },
    retry: (failureCount, error) => {
      const msg = error instanceof Error ? error.message : ''
      if (msg.includes('Authentication failed')) {
        return false
      }
      return failureCount < 2
    },
  })

  return {
    data: mutation.data,
    error: mutation.error,
    isLoading: mutation.isPending || authLoading,
    checkEligibility: mutation.mutateAsync,
    reset: mutation.reset,
    kycStatusData: kycStatusMutation.data,
    kycStatusError: kycStatusMutation.error,
    isKycStatusLoading: kycStatusMutation.isPending,
    checkKycStatus: kycStatusMutation.mutateAsync,
    resetKycStatus: kycStatusMutation.reset,
  }
}
